/**
 * navigation/surfaceScreens.tsx
 * Single registry mapping each surface key to its screen component, plus a
 * renderer that shows the router's active surface. Keeping the map in one place
 * means the sidebar, router, and renderer never drift.
 */

import React from 'react';
import { View, Text, TouchableOpacity } from 'react-native';
import type { SurfaceKey } from '../queue/surfaces';
import { getSurface } from '../queue/surfaces';
import { useTheme } from '../context/themeContext';
import { Mono } from '../queue/components/atoms';
import { useSurfaceRouter } from './surfaceRouter';

import DashboardScreen from '../screens/dashboard';
import QueueDetailScreen from '../screens/queueDetail';
import GuestScreen from '../screens/guest';
import BoardScreen from '../screens/board';
import HostConsoleScreen from '../screens/hostConsole';
import VoiceScreen from '../screens/voice';
import ChatScreen from '../screens/chat';

// rev 5: engine surfaces (Dashboard, Queue Detail) are the admin nav; the rest
// are PRODUCT-SURFACE previews. The ex-admin Operator/Config/Live View screens
// are folded into Queue Detail's Controls/Setup/Overview sub-tabs.
export const SURFACE_SCREENS: Record<SurfaceKey, React.ComponentType> = {
  dashboard: DashboardScreen,
  detail: QueueDetailScreen,
  guest: GuestScreen,
  board: BoardScreen,
  host: HostConsoleScreen,
  voice: VoiceScreen,
  chat: ChatScreen,
};

/**
 * PRODUCT-SURFACE banner (rev 5). When an admin opens a product surface (Guest,
 * Host, Board, Voice, Chat) as a PREVIEW from Queue Detail → Overview, we flag
 * that the engine ships the data + API, not this screen — the consuming product
 * builds it. A back button returns to Queue Detail.
 */
function PreviewBanner() {
  const { theme } = useTheme();
  const { colors } = theme;
  const { setSurface } = useSurfaceRouter();
  // Light dashed pill (prototype): "← Queue" button + PRODUCT SURFACE tag + message.
  return (
    <View style={{ paddingHorizontal: 16, paddingTop: 14 }}>
      <View style={{
        flexDirection: 'row', alignItems: 'center', gap: 9, paddingVertical: 8, paddingHorizontal: 14,
        backgroundColor: colors.paper2, borderWidth: 1, borderStyle: 'dashed', borderColor: colors.lineStrong, borderRadius: 10,
      }}>
        <TouchableOpacity onPress={() => setSurface('detail')}
          style={{ backgroundColor: colors.card, borderWidth: 1, borderColor: colors.lineStrong, borderRadius: 6, paddingHorizontal: 9, paddingVertical: 3 }}>
          <Text style={{ fontSize: 11, color: colors.ink }}>← Queue</Text>
        </TouchableOpacity>
        <View style={{ backgroundColor: colors.card, borderWidth: 1, borderColor: colors.lineStrong, borderRadius: 5, paddingHorizontal: 7, paddingVertical: 2 }}>
          <Mono style={{ fontSize: 10, fontWeight: '600', color: colors.ink }}>PRODUCT SURFACE</Mono>
        </View>
        <Text style={{ flex: 1, fontSize: 12, color: colors.ink3 }} numberOfLines={2}>
          The consuming product builds this screen on the SK Queue API — the engine ships the data + allocation, not this UI.
        </Text>
      </View>
    </View>
  );
}

export function ActiveSurface() {
  const { surface } = useSurfaceRouter();
  const Screen = SURFACE_SCREENS[surface];
  const isPreview = !getSurface(surface).engine;
  if (isPreview) {
    return (
      <View style={{ flex: 1, minHeight: 0 }}>
        <PreviewBanner />
        <View style={{ flex: 1, minHeight: 0 }}>
          <Screen />
        </View>
      </View>
    );
  }
  return <Screen />;
}
